import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { gql } from 'apollo-angular';
import { catchError, Observable, Subject, throwError } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { AnalyticsServicesNameEnum } from 'src/app/core/services/analytics/enums/analytics-services-name.enum';
import {
  DAY_OF_WEEK_DESCRIPTION,
  TIME_OF_DAY_DESCRIPTION
} from 'src/app/pages/agreements/constants/rate-description.constants';
import { AgreementAnalyticsPropertiesService } from 'src/app/pages/agreements/services/agreement-analytics.properties.service';
import {
  ClientProperties,
  InitiatedByProperties,
  SpProperties
} from 'src/app/pages/agreements/types/agreement-analytics-properties.model';
import { ExceededRateReason } from 'src/app/pages/agreements/types/exceeded-rate';
import { TerminateAgreementMutationResult } from 'src/app/shared/graphql/agreements/terminate-agreement-mutation.graphql';
import { TerminateAgreementMutationService } from 'src/app/shared/graphql/agreements/terminate-agreement-mutation.graphql.autogenerated';
import { ServiceCategoryableType } from 'src/app/shared/graphql/graphql-types';
import { Agreement, Coordinator } from 'src/app/shared/models/gql/agreement.model';
import { CreateOrRequestAgreement } from 'src/app/shared/models/gql/create-or-request-agreement';
import { Organisation } from 'src/app/shared/models/gql/organisation.model';
import { RateChecker } from 'src/app/shared/models/gql/rate-checker.model';
import { BudgetSupportType, RateSchedule } from 'src/app/shared/models/gql/rate.model';
import { User } from 'src/app/shared/models/gql/user.model';
import { NewAgreementData } from 'src/app/shared/models/new-agreement.model';
import { NetRate } from 'src/app/shared/models/price-limit-alert-data.model';
import { ApiInterfaceService } from 'src/app/shared/services/api-interface.service';
import { ChatService } from 'src/app/shared/services/chat.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { CircumventionModalData } from 'src/app/shared/types/circumvention-modal-data';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AgreementsService {
  // when fired, lets subscribers know that the agreement list needs refreshing.
  public refreshList: Subject<void> = new Subject();

  constructor(
    private alertCtrl: AlertController,
    private api: ApiInterfaceService,
    private utilityService: UtilityService,
    private chatService: ChatService,
    private analyticsService: AnalyticsService,
    private analyticsPropertiesService: AgreementAnalyticsPropertiesService,
    private terminateAgreementMutationService: TerminateAgreementMutationService,
  ) {}

  /**
   * Fire an event to let other pages know that they need to refresh their agreement data.
   */
  refresh() {
    this.refreshList.next();
  }

  /**
   * Delegate to the analytics properties service to get the SP related properties for the agreement events.
   *
   * @param agreement - The Agreement.
   */
  analyticsSpProperties(agreement: Agreement): SpProperties {
    return this.analyticsPropertiesService.spProperties(agreement);
  }

  /**
   * Delegate to the analytics properties service to get the client related properties for the agreement events.
   *
   * @param clientUuid - The UUID of the client.
   * @param jobUuid - The ID of the job.
   * @param agreementUuid - The ID of the agreement.
   */
  analyticsClientProperties(clientUuid: string, jobUuid: string | undefined, agreementUuid: string): ClientProperties {
    return this.analyticsPropertiesService.clientProperties(clientUuid, jobUuid, agreementUuid);
  }

  /**
   * Delegate to the analytics properties service to get the client related properties for the agreement events.
   *
   * @param clientUuid.clientUuid
   * @param clientUuid - The UUID of the client.
   * @param agreementUuid - The ID of the agreement .
   * @param jobUuid - The ID of the job.
   * @param spUuid - The UUID of the service provider.
   * @param initiatiedByUserType - The type of user initiating the agreement.
   * @param clientUuid.agreementUuid
   * @param clientUuid.jobUuid
   * @param clientUuid.spUuid
   * @param clientUuid.initiatedByUserType
   */
  analyticsInitiatedByProperties({
    clientUuid,
    agreementUuid,
    jobUuid,
    spUuid,
    initiatedByUserType,
  }: {
    clientUuid: string;
    agreementUuid?: string;
    jobUuid?: string;
    spUuid: string;
    initiatedByUserType?: string;
  }): InitiatedByProperties {
    return this.analyticsPropertiesService.initiatedByProperties(
      clientUuid,
      agreementUuid,
      jobUuid,
      spUuid,
      initiatedByUserType,
    );
  }

  /**
   * fetching latest my workers or my client list data.
   *
   * @param type
   * @param [after] - It will contain endCursor of page info and used for pagination.
   */
  async getAgreementList(type: 'myClients' | 'myWorkers', after?): Promise<any> {
    try {
      const res = await this.api.gqlQuery(
        gql`
      query ${type}($after: String, $first: Int) {
        ${type}(first: $first, after: $after) {
      nodes {
        id
        otherUser {
          id
          uid
          avatar {
            small
          }
          firstName
          fullName
          displayName
          userHasBlocked
          userIsBlocked
          profile{
            ...on ClientProfile{
              typeOfCare
            }
          }
        }
        status
        type
        conversation {
          sendbirdChannelDetail {
            channelUrl
          }
        }
        }
        }
        user {
          profile {
            ... on ClientProfile {
              typeOfCare
            }
          }
        }
      }`,
        {
          first: null,
          after,
        },
      );
      return res;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * fetching active agreements
   *
   * @param type
   * @param [after] - It will contain endCursor of page info and used for pagination.
   */
  async getActiveAgreementList(type: 'myClients' | 'myWorkers', after?): Promise<any> {
    try {
      const res = await this.getAgreementList(type, after);
      const activeAgreementStatuses = ['accepted', 'amendments_declined', 'amendments_accepted', 'agreement_amended'];
      res[type].nodes = res[type].nodes.filter((x) => activeAgreementStatuses.indexOf(x.status) > -1);
      return res;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * To fetch agreement amendment details using agreement id.
   *
   * @param id - Id for get agreement detail
   * @returns - Agreement Amendment
   */
  async getAgreementAmendmentById(id: number | string): Promise<Agreement['agreementAmendment']> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query agreement($id: ID!) {
            agreement(id: $id) {
              agreementAmendment {
                quickpayEnabled
                id
                agreedService
                agreementRates {
                  id
                  clientNetDollars
                  dollars
                  flatRate
                  grossDollars
                  rateCents
                  rateName
                  workerNetDollars
                  rateDescription
                  rateSchedule {
                    weekday
                    saturday
                    sunday
                    publicHoliday
                    daytime
                    evening
                    overnight
                  }
                  budgetSupportTypes {
                    id
                    name
                    displayName
                  }
                }
                jobOccurrence
                jobFrequency
                agreedOnSchedule
                expectedEndDate
                expectedStartDate
                scheduleDescription
                scheduleSummary
                sessionSummaries
                agreedOnSchedule
                daysAndTimes {
                  monday {
                    expected
                    expectedSessions {
                      startAt
                      endAt
                    }
                  }
                  tuesday {
                    expected
                    expectedSessions {
                      startAt
                      endAt
                    }
                  }
                  wednesday {
                    expected
                    expectedSessions {
                      startAt
                      endAt
                    }
                  }
                  thursday {
                    expected
                    expectedSessions {
                      startAt
                      endAt
                    }
                  }
                  friday {
                    expected
                    expectedSessions {
                      startAt
                      endAt
                    }
                  }
                  saturday {
                    expected
                    expectedSessions {
                      startAt
                      endAt
                    }
                  }
                  sunday {
                    expected
                    expectedSessions {
                      startAt
                      endAt
                    }
                  }
                }
              }
            }
          }
        `,
        { id },
      );
      return res['agreement']['agreementAmendment'];
    } catch (error) {
      return Promise.reject(error?.error);
    }
  }

  /**
   * To fetch agreement amendment details using agreement id.
   *
   * @param id - Id for get agreement detail
   */
  async getAgreementById(id: number | string): Promise<Agreement> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query agreement($id: ID!) {
            agreement(id: $id) {
              agreedService
              id
              isRequested
              isAccepted
              availableActions {
                canAcceptAgreement
              }
              conversation {
                id
                sendbirdChannelDetail {
                  channelUrl
                }
                hasAgreement
              }
              currentUser {
                id
                uuid
                firstName
                displayName
                isOnboardingComplete
                avatar {
                  small
                }
                profile {
                  ... on ClientProfile {
                    typeOfCare
                    participantRelationship
                    onboardingStatusPercentage
                    paymentMethodVerified
                  }
                  ... on WorkerProfile {
                    id
                  }
                }
                participants {
                  firstName
                  lastName
                  gender
                  age
                  contactPhone
                }
              }
              otherUser {
                id
                uuid
                firstName
                displayName
                isHcpFunded
                ndisAgencyManaged
                accountType
                avatar {
                  small
                }
                profile {
                  ... on ClientProfile {
                    typeOfCare
                    participantRelationship
                    paymentMethodVerified
                  }
                  ... on WorkerProfile {
                    id
                  }
                }
                participants {
                  firstName
                  lastName
                  gender
                  age
                  contactPhone
                }
                quickpayInsured
              }
              agreementRates {
                id
                rateCents
                clientNetDollars
                workerNetDollars
                rateName
                flatRate
                grossDollars
                rateDescription
                rateSchedule {
                  weekday
                  saturday
                  sunday
                  publicHoliday
                  daytime
                  evening
                  overnight
                }
                budgetSupportTypes {
                  id
                  name
                  displayName
                }
              }
              status
              ndisPlan {
                id
              }
              job {
                id
                uuid
                hoursDescription
                title
                jobKind {
                  kind
                  isFlex
                }
                title
                location {
                  address
                }
                hours
                days {
                  summary
                }
                badges {
                  days
                  details
                  hours
                  summary
                  organisation
                }
                workerFields {
                  allowActions {
                    archive
                    decline
                    reply
                    report
                  }
                }
                status {
                  workerStatus
                  clientStatus
                  clientState
                  clientStatusDescription
                }
                serviceCategories {
                  name
                }
                participant {
                  age
                }
                frequency
                timestamps {
                  createdAt
                  startTime
                  endTime
                }
              }
              jobOccurrence
              jobFrequency
              scheduleSummary
              sessionSummaries
              agreedOnSchedule
              expectedEndDate
              expectedStartDate
              scheduleDescription
              daysAndTimes {
                monday {
                  expected
                  expectedSessions {
                    startAt
                    endAt
                  }
                }
                tuesday {
                  expected
                  expectedSessions {
                    startAt
                    endAt
                  }
                }
                wednesday {
                  expected
                  expectedSessions {
                    startAt
                    endAt
                  }
                }
                thursday {
                  expected
                  expectedSessions {
                    startAt
                    endAt
                  }
                }
                friday {
                  expected
                  expectedSessions {
                    startAt
                    endAt
                  }
                }
                saturday {
                  expected
                  expectedSessions {
                    startAt
                    endAt
                  }
                }
                sunday {
                  expected
                  expectedSessions {
                    startAt
                    endAt
                  }
                }
              }
              quickpayEnabled
              travelExpenseFlag
              perKmRateInDollars
              uuid
              grandfatheredAt
            }
          }
        `,
        { id },
      );
      return res['agreement'];
    } catch (error) {
      return Promise.reject(error?.error);
    }
  }

  /**
   * Sends a GET request to fetch coordinator from agreement
   *
   * @param id - Agreement id
   */
  async getCoordinator(id): Promise<Coordinator> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query agreement($id: ID!) {
            agreement(id: $id) {
              otherUser {
                coordinator {
                  truncatedName
                }
              }
            }
          }
        `,
        {
          id,
        },
      );
      return res['agreement']['otherUser']['coordinator'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sends a GET request to fetch coordinator from agreement
   *
   * @param id - Agreement id
   */
  async getOtherUser(id: number | string): Promise<User> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query agreement($id: ID!) {
            agreement(id: $id) {
              otherUser {
                organisation {
                  businessName
                }
                accountType
                displayName
                coordinator {
                  truncatedName
                }
              }
            }
          }
        `,
        {
          id,
        },
      );
      return res['agreement']['otherUser'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Get support term fields for a given agreement ID
   *
   * @param id - agreement ID
   * @returns - Agreement promise containing only the term fields
   */
  async getAgreementSupportTermsById(id: number | string): Promise<Agreement> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query agreement($id: ID!) {
            agreement(id: $id) {
              supportCancel
              supportChange
              supportMinHours
              supportFeedback
              agreementAmendment {
                supportCancel
                supportChange
                supportMinHours
                supportFeedback
              }
            }
          }
        `,
        { id },
      );
      return res['agreement'];
    } catch (error) {
      return Promise.reject(error?.error);
    }
  }

  /**
   * Create new agreement
   *
   * @param data - agreement object
   */
  async createAgreement(data: NewAgreementData): Promise<Agreement> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation sendOffer($SendOfferInput: SendOfferInput!) {
            sendOffer(input: $SendOfferInput) {
              agreement {
                id
                uuid
                job {
                  uuid
                }
              }
            }
          }
        `,
        { SendOfferInput: data },
      );
      this.refresh();
      return res['sendOffer']['agreement'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * send request to update an agreement.
   *
   * @param agreement - object
   * @param newAgr
   */
  async updateOffer(newAgr): Promise<Agreement> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation updateOffer($UpdateOfferInput: UpdateOfferInput!) {
            updateOffer(input: $UpdateOfferInput) {
              agreement {
                id
              }
            }
          }
        `,
        { UpdateOfferInput: newAgr },
      );
      this.refresh();
      return res['updateOffer']['agreement'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * send request to amend an agreement
   *
   * @param agreement - object
   * @param newAgr
   */
  async amendAgreement(newAgr): Promise<Agreement> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation amendAgreement($AmendAgreementInput: AmendAgreementInput!) {
            amendAgreement(input: $AmendAgreementInput) {
              agreement {
                id
              }
            }
          }
        `,
        { AmendAgreementInput: newAgr },
      );
      this.refresh();
      return res['amendAgreement']['agreement'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sends a graphql mutation to terminate agreement.
   *
   * @param agreement - Agreement object for terminate agreement request.
   */
  destroy(agreement: Agreement): Observable<TerminateAgreementMutationResult> {
    return this.terminateAgreementMutationService
      .mutate({
        TerminateAgreementInput: {
          id: agreement.id.toString(),
          reason: {},
        },
      })
      .pipe(
        take(1),
        map((response) => {
          this.refresh();
          return response.data.terminateAgreement.agreement;
        }),
        catchError((error) => {
          console.error('[Terminate Agreement] Failed to terminate agreement');
          return throwError(() => error);
        }),
      );
  }

  async acceptOfferOrAmendments(agreement: Agreement): Promise<any> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation respondToAgreementOffer($RespondToAgreementOfferInput: RespondToAgreementOfferInput!) {
            respondToAgreementOffer(input: $RespondToAgreementOfferInput) {
              clientMutationId
              agreement {
                sessionsPlanned
              }
            }
          }
        `,
        {
          RespondToAgreementOfferInput: {
            id: agreement.id,
            action: 'accept',
          },
        },
      );
      this.refresh();
      return res;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async deleteOffer(agreement: Agreement): Promise<any> {
    try {
      const res = await (<any>(await this.api.post(`/agreements/${agreement.id}/decline_offer.json`, null, true)).toPromise());
      this.refresh();
      return res;
    } catch (error) {
      return Promise.reject(error?.error);
    }
  }

  async declineOfferOrAmendments(agreement: Agreement): Promise<any> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation respondToAgreementOffer($RespondToAgreementOfferInput: RespondToAgreementOfferInput!) {
            respondToAgreementOffer(input: $RespondToAgreementOfferInput) {
              clientMutationId
            }
          }
        `,
        {
          RespondToAgreementOfferInput: {
            id: agreement.id,
            action: 'decline',
          },
        },
      );
      this.refresh();
      return res;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Decline requested offer that get from client.
   *
   * @param - - Agreement object for decline agreement request.
   * @param agreement
   */
  async declineRequest(agreement: Agreement): Promise<any> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation declineAgreement($id: ID!) {
            declineAgreement(input: { id: $id }) {
              agreement {
                id
                status
              }
            }
          }
        `,
        { id: agreement.id },
      );
      this.refresh();
      return res['declineAgreement'];
    } catch (error) {
      return Promise.reject(error?.error);
    }
  }

  /**
   * Check is conversation has permission to create agreement
   *
   * @param id
   */
  async canCreateAgreement(id: number | string) {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query conversationDetails($id: ID!) {
            conversationDetails(id: $id) {
              allowedActions {
                createAgreement {
                  isAllowed
                  errors {
                    errorMessage
                    category
                    moreInfoLink
                    externalInfoLink
                  }
                }
              }
              sendbirdChannelDetail {
                channelUrl
              }
            }
          }
        `,
        { id },
      );

      const canCreate: CreateOrRequestAgreement = res['conversationDetails']['allowedActions']['createAgreement'];

      // Check if worker has exchanged conversation with client
      // This check has been moved to frontend as we are now relying on Sendbird for message exchange.
      if (canCreate.isAllowed) {
        canCreate.isAllowed = await this.chatService.isMessagesExchanged(
          res['conversationDetails']?.sendbirdChannelDetail?.channelUrl,
          ['message'],
        );
        // Set error if not allowed
        if (!canCreate.isAllowed) {
          canCreate.errors = [
            {
              category: 'messageExchange',
              errorMessage:
                "To send an agreement to a client you'll need to start conversation and the client respond.",
            },
          ];
        }
      }
      if (!canCreate.isAllowed) {
        // Shows two types of alerts depending on whether moreInfoLink is true. showAlert only has an Okay action whereas
        // alertCtrl.create has two cta buttons: cancel and find out more
        const error = canCreate.errors[0]; // show an alert with the first error (at time of writing, canCreateAgreement can only return a single error)
        let buttons = [];
        if (!error.moreInfoLink) {
          buttons = [{ text: 'Ok' }];
        } else {
          buttons = [
            { text: 'Cancel', role: 'cancel' },
            {
              text: 'Find out more',
              handler: () => {
                this.utilityService.openExternalLink(environment.webAppUrl() + error.moreInfoLink);
              },
            },
          ];
        }
        (
          await this.alertCtrl.create({
            header: 'Agreement Requirement',
            message: error?.errorMessage,
            buttons,
          })
        ).present();
      }
      return canCreate;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Get calculated rate from backend.
   *
   * @param {RateChecker} rate - Rate Checker object for getting net rate.
   * @returns {NetRate} - Net Rate object containing net rate and price limit information.
   */
  async getRates(rate: RateChecker): Promise<NetRate> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query calculatedRate(
            $agreementId: ID
            $baseRate: Float!
            $consumerId: ID
            $grandfatheredAt: ISO8601DateTime
            $timeAndPlace: TimeAndPlace
            $budgetSupportTypes: [ID!]
            $postedJobId: ID
          ) {
            calculatedRate(
              agreementId: $agreementId
              baseRate: $baseRate
              consumerId: $consumerId
              grandfatheredAt: $grandfatheredAt
              timeAndPlace: $timeAndPlace
              budgetSupportTypes: $budgetSupportTypes
              postedJobId: $postedJobId
            ) {
              rateWithFee
              exceedCapRate
              priceLimitAlertType
              priceLimitAlertToDType
              capRate
              supportLocation
              supportType
              supportName
            }
          }
        `,
        rate,
      );
      return res['calculatedRate'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Get organisation terms and conditions
   *
   * @param {string | number} consumerId - Customer user ID.
   */
  async getOrganisationTerms(consumerId: string | number): Promise<Organisation> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query userOrganisationTerm($consumerId: ID!) {
            userOrganisationTerm(consumerId: $consumerId) {
              id
              email
              phoneNumber
              contentOfTerms
              carerFirstName
              consumerFirstName
              organisationName
              status
              organisationEmail
              organisationPhone
              isClientLinkedToOrg
              isShowingTermPopup
            }
          }
        `,
        {
          consumerId,
        },
      );
      return res['userOrganisationTerm'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Update the status of organisation terms.
   *
   * @param userOrganisationTermId - Contains organisation term id.
   * @param action - Contains action detail to 'Accept' or 'Cancel' terms.
   */
  async acceptOrCancelUserOrganisationTerm(
    userOrganisationTermId: string | number,
    action: 'accepted' | 'cancelled',
  ): Promise<Organisation> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation acceptCancelUserOrganisationTerm(
            $acceptCancelUserOrganisationTermInput: AcceptCancelUserOrganisationTermInput!
          ) {
            acceptCancelUserOrganisationTerm(input: $acceptCancelUserOrganisationTermInput) {
              userOrganisationTerm {
                id
                contentOfTerms
                status
              }
            }
          }
        `,
        { acceptCancelUserOrganisationTermInput: { userOrganisationTermId, action } },
      );
      return res['acceptCancelUserOrganisationTerm']['userOrganisationTerm'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sends a request to validate per km rate.
   *
   * @param perKmRateDollars - Per km rate that we need to validate.
   */
  async validatePerKmTravelRate(perKmRateDollars: number): Promise<{ perKmRateValid: boolean; message: string }> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query validatePerKmRate($perKmRateDollars: Float!) {
            validatePerKmRate(perKmRateDollars: $perKmRateDollars) {
              perKmRateValid
              message
            }
          }
        `,
        {
          perKmRateDollars,
        },
      );
      return res['validatePerKmRate'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * To fetch amended agreement travel details.|
   * Created separate method as the getAgreementById method already has complexity.
   *
   * @param id - Id for get agreement related detail
   */
  async getAgreementAmendTravelDetail(id: number | string): Promise<Partial<Agreement>> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query agreement($id: ID!) {
            agreement(id: $id) {
              uuid
              agreementAmendment {
                travelExpenseFlag
                perKmRateInDollars
              }
              otherUser {
                disablePerKmTravel
                privatlyFunded
                ndisNonNdia
                uuid
              }
            }
          }
        `,
        { id },
      );
      return res['agreement'];
    } catch (error) {
      return Promise.reject(error?.error);
    }
  }

  /**
   * To fetch agreement fee percentage.
   * Created separate method as the getAgreementById method already has complexity.
   *
   * @param id - Id for get agreement related detail
   */
  async getAgreementFeePercentage(id: number | string): Promise<string> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query agreement($id: ID!) {
            agreement(id: $id) {
              feePercentage
            }
          }
        `,
        { id },
      );
      return res['agreement']['feePercentage'];
    } catch (error) {
      return Promise.reject(error?.error);
    }
  }

  /**
   * Derive the day of week description from the rate schedule provided.
   *
   * @param rateSchedule - Object containing day of the week and time of the day selections for the rate schedule.
   * @param getDay - Return the day (property) instead of description (value) when set to true, default is false.
   * @returns - Day of Week description in string format.
   */
  getDayOfWeekDescription(rateSchedule: RateSchedule, getDay = false): string {
    return Object.entries(DAY_OF_WEEK_DESCRIPTION)
      .reduce((acc, [day, description]) => {
        if (rateSchedule && rateSchedule[day]) acc.push(getDay ? day : description);
        return acc;
      }, [])
      .join(', ');
  }

  /**
   * Derive the time of day description from the rate schedule provided.
   *
   * @param rateSchedule - Object containing day of the week and time of the day selections for the rate schedule.
   * @param getTime - Return the time (property) instead of description (value) when set to true, default is false.
   * @returns - Time of Day description in string format.
   */
  getTimeOfDayDescription(rateSchedule: RateSchedule, getTime = false): string {
    return Object.entries(TIME_OF_DAY_DESCRIPTION)
      .reduce((acc, [time, description]) => {
        if (rateSchedule && rateSchedule[time]) acc.push(getTime ? time : description);
        return acc;
      }, [])
      .join(', ');
  }

  /**
   * Call agreement related segment events
   *
   * @param segmentEvent - Event for segment event
   * @param segmentData - Properties which require to pass in segment
   * @param setPriceLimitType - Check if require to pass pricelimitalert in properties
   */
  agreementSegmentCall(segmentEvent, segmentData, setPriceLimitType): void {
    const properties = {
      agreementId: segmentData.agreementId || '',
      agreedRate: segmentData.rate.rateCents || '',
      agreedTempRateId: segmentData.rate.tempRateId || '',
      agreedRateId: segmentData.rate.id || '',
      agreedRateName: segmentData.rate.rateName,
      dayOfWeekSelected: this.getDayOfWeekDescription(segmentData.rate.rateSchedule, true) || '',
      timeOfDaySelected: this.getTimeOfDayDescription(segmentData.rate.rateSchedule, true) || '',
      clientId: segmentData.clientId,
      rateType: segmentData.rate.flatRate ? 'flat' : 'hourly',
      workerType: segmentData.workerType,
      agreementType: 'Standard',
      legacyAgreement: segmentData.legacyAgreement,
      supportTypes: segmentData.rate.budgetSupportTypes
        ? segmentData.rate.budgetSupportTypes.map((supportType) => supportType.name).toString()
        : '',
    };

    if (setPriceLimitType) {
      properties['priceLimitAlertType'] = segmentData.priceLimitAlertType;
    }

    this.analyticsService.track(segmentEvent, properties, AnalyticsServicesNameEnum.SEGMENT_SERVICE);
  }

  /**
   * Given the job ID and consumer user ID and, return the list of default and available support types.
   *
   * @param postedJobId - Job Id
   * @param consumerUserId - consumer user ID
   * @returns - An object with array of default budget support types and array of available budget support types
   */
  async getBudgetSupportTypes(
    postedJobId: string | number,
    consumerUserId: number,
  ): Promise<{ availableBudgetSupportTypes: BudgetSupportType[]; defaultBudgetSupportTypes: BudgetSupportType[] }> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query budgetSupportTypes($postedJobId: ID, $consumerUserId: ID) {
            budgetSupportTypes(postedJobId: $postedJobId, consumerUserId: $consumerUserId) {
              defaultBudgetSupportTypes {
                id
                name
                displayName
                isAdminOnlyType
              }
              availableBudgetSupportTypes {
                id
                name
                displayName
                isAdminOnlyType
              }
            }
          }
        `,
        { postedJobId, consumerUserId },
      );
      return res['budgetSupportTypes'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Saves the reason why SP is exceeding a NDIS max price limit for a given rate.
   *
   * @param exceededRateReasonRequestObj - object containing the properties needed to save the exceeded reason.
   * @returns status of the call: true if successful, false otherwise.
   */
  async saveExceededRateReason(exceededRateReasonRequestObj: ExceededRateReason): Promise<boolean> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation createExceededRateReason($exceededRateReasonAttributes: ExceededRateReasonAttributes!) {
            createExceededRateReason(input: { exceededRateReasonAttributes: $exceededRateReasonAttributes }) {
              status
            }
          }
        `,
        { exceededRateReasonAttributes: exceededRateReasonRequestObj },
      );
      return res['createExceededRateReason']['status'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
 * Check the service categories that are associated with the
 * agreement. Returns true if there are budget support types in
 * the agreement.
 *
 *  @param agreement -  Agreement.
 */
  canShowBudgetSupportTypeInformation(agreement: Agreement): boolean {
    if(agreement.agreementRates?.length) {
      const hourlyRates = agreement.agreementRates.filter((rate) => !rate.flatRate);
      return !!hourlyRates[0]?.budgetSupportTypes?.length;
    }
    if (agreement.serviceGroups?.length) {
      const hasNDISAgreedRate = agreement.serviceGroups.some((serviceGroup) =>
        serviceGroup.find(
          (agreedRate) => agreedRate.serviceCategoryable.serviceType === ServiceCategoryableType.NdisServiceCategory,
        ),
      );

      return hasNDISAgreedRate;
    }
    return false;
  }

   /**
   * To fetch client payment verification details from agreement.
   *
   * @param id - Id for get agreement detail
   */
   async getClientPaymentVerificationInfo(id: number | string): Promise<Agreement> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query agreement($id: ID!) {
            agreement(id: $id) {
              otherUser {
                profile {
                  ... on ClientProfile {
                    paymentMethodVerified
                  }
                }
              }
            }
          }
        `,
        { id },
      );
      return res['agreement'];
    } catch (error) {
      return Promise.reject(error?.error);
    }
  }

  getConsumerModalData(): CircumventionModalData {
    return {
      steps: [
        {
          step: 1,
          imagePath: 'assets/img/mable/agreement-creation-success.svg',
          title: 'Congratulations on finding a new support worker!',
          description:
            'Now that you have an agreement, please remember that all work must be managed through the Mable platform. This keeps everything simple and everyone safe.',
          additionalInfo:
            'If you arrange work or payments with this worker outside of Mable, you will be breaching our <a href="https://mable.com.au/legal/#terms" target="_blank" rel="noopener noreferrer">terms of use</a> which might lead to a suspension or ban. This rule is in place for 12 months after your last contact with them.',
          nextButtonLabel: 'Next',
        },
        {
          step: 2,
          title: 'Managing sessions on Mable',
          imagePath: 'assets/img/mable/insurance.svg',
          description: `After each session you’ll need to:
<ul>
<li>Approve the support hours.</li>
<li>Book future sessions through Mable.</li>
</ul>
Using Mable protects both you and your support worker. They are covered by one of the most complete <a href="https://mable.com.au/insurance/" target="_blank" rel="noopener noreferrer">insurance policies</a> that we arrange for them.`,
          additionalInfo:
            'People working outside our platform may have little or no insurance, which is a risk for everyone.',
          nextButtonLabel: 'Next',
          backButtonLabel: 'Back',
        },
        {
          step: 3,
          title: 'Continued support on Mable',
          imagePath: 'assets/img/mable/expectation-management.svg',
          description: `On Mable you continue to access benefits like:
<ul>
<li>Access to verified support workers.</li>
<li>Secure payments.</li>
<li>Help from our expert team.</li>
</ul>`,
          additionalInfo:
            'If you’d like to find additional support, we’re here to help. Post a job or search workers to find a backup or build a team.',
          nextButtonLabel: 'OK, got it',
          backButtonLabel: 'Back',
        },
      ],
    };
  }
}
